import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthSession, type SessionUser } from "@/lib/auth-client";
import { api } from "@/lib/api";
import { queryKeys } from "@/hooks/queryKeys";
import type { DashboardSummary } from "@/types/dashboard";
import { SummaryCards } from "@/components/SummaryCards";
import { AgreementExpiryBanner } from "@/components/AgreementExpiryBanner";
import { ExpenseList } from "@/components/ExpenseList";
import { Typography } from "@/components/ui/Typography";

const EMPTY_SUMMARY: DashboardSummary = {
  totalProperties: 0,
  totalMonthlyRent: 0,
  pendingPayments: 0,
  overduePayments: 0,
  upcomingExpiries: [],
  recentExpenses: [],
  collectionRate: 0,
};

const DASHBOARD_SUMMARY_CACHE_MS = 2 * 60 * 1000; // 2 minutes

export function DashboardPage() {
  const { session } = useAuthSession();
  const user = session?.user as SessionUser | undefined;
  const queryClient = useQueryClient();
  const name = user?.name?.trim() || user?.email?.split("@")[0] || "there";

  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);

    const key = queryKeys.dashboardSummary();
    const cached = queryClient.getQueryData<DashboardSummary>(key);
    const state = queryClient.getQueryState(key);
    const cacheAge = state?.dataUpdatedAt ? Date.now() - state.dataUpdatedAt : Infinity;
    if (cached != null && cacheAge < DASHBOARD_SUMMARY_CACHE_MS) {
      setSummary(cached);
      return;
    }

    api
      .get<DashboardSummary>("/dashboard/summary")
      .then((data) => {
        if (!cancelled) {
          const value = data ?? EMPTY_SUMMARY;
          setSummary(value);
          queryClient.setQueryData(key, value);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err : new Error(String(err)));
      });
    return () => {
      cancelled = true;
    };
  }, [queryClient]);

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
        <p className="text-red-700 font-medium">{error.message}</p>
        <p className="mt-2 text-sm text-red-600">Please try again later.</p>
      </div>
    );
  }

  if (summary === null) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-slate-200 dark:bg-slate-700 rounded-lg animate-pulse" />
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-28 bg-slate-200 dark:bg-slate-700 rounded-2xl animate-pulse" />
          ))}
        </div>
        <div className="h-64 bg-slate-200 dark:bg-slate-700 rounded-2xl animate-pulse" />
      </div>
    );
  }

  const resolvedSummary = summary;

  return (
    <div className="space-y-6">
      {/* Greeting */}
      <div>
        <Typography variant="h1">Welcome back, {name}</Typography>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Here&apos;s an overview of your rental portfolio.
        </p>
      </div>

      {/* Summary Cards */}
      <SummaryCards
        totalProperties={resolvedSummary.totalProperties}
        totalMonthlyRent={resolvedSummary.totalMonthlyRent}
        pendingPayments={resolvedSummary.pendingPayments}
        overduePayments={resolvedSummary.overduePayments}
        collectionRate={resolvedSummary.collectionRate}
      />

      {/* Agreement expiry banner */}
      {resolvedSummary.upcomingExpiries.length > 0 && (
        <AgreementExpiryBanner expiries={resolvedSummary.upcomingExpiries} />
      )}

      {/* Recent expenses */}
      <ExpenseList expenses={resolvedSummary.recentExpenses} />

      {/* Empty state */}
      {resolvedSummary.totalProperties === 0 && (
        <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700/80 bg-white dark:bg-slate-800/95 p-10 sm:p-16 text-center shadow-sm dark:shadow-none">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-50 dark:bg-primary-900/30">
            <svg
              className="h-8 w-8 text-primary-500 dark:text-primary-400"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8.25 21v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21m0 0h4.5V3.545M12.75 21h7.5V10.75M2.25 21h1.5m18 0h-18M2.25 9l4.5-1.636M18.75 3l-1.5.545m0 6.205 3 1m1.5.5-1.5-.5M6.75 7.364V3h-3v18m3-13.636 10.5-3.819"
              />
            </svg>
          </div>
          <Typography variant="h3" className="mt-5">No properties yet</Typography>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 max-w-sm mx-auto leading-relaxed">
            Add your first property to start tracking rent and expenses.
          </p>
          <Link
            to="/properties/new"
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm shadow-primary-600/20 hover:bg-primary-700 active:scale-[0.98] transition-all"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            Add Property
          </Link>
        </div>
      )}
    </div>
  );
}
